import { StatusBadge } from '@chiroup/components/StatusBadge';
import { SeverityEnum } from '@chiroup/components/enums/severity';
import {
  BillingReadinessSeverity,
  BillingReadinessWindow,
} from '@chiroup/core/types/BillingReadiness.type';
import {
  BanknotesIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { useBillingReadinessDetail } from '../../hooks/useBillingReadiness';
import Modal from '../common/Modal';

const SEVERITY: Record<BillingReadinessSeverity, SeverityEnum> = {
  [BillingReadinessSeverity.Critical]: SeverityEnum.Error,
  [BillingReadinessSeverity.Moderate]: SeverityEnum.Warning,
  [BillingReadinessSeverity.Normal]: SeverityEnum.Success,
  [BillingReadinessSeverity.None]: SeverityEnum.Note,
};

const Badge: React.FC<{ severity: BillingReadinessSeverity; text: string }> = ({
  severity,
  text,
}) => <StatusBadge severity={SEVERITY[severity]} status={text} />;

// periodTimestamp is midnight GMT — format in UTC so the day doesn't shift.
const fmtDate = (ts: number | null) =>
  ts == null ? '—' : new Date(ts).toLocaleDateString('en-US', { timeZone: 'UTC' });

const muted = 'text-gray-400 dark:text-dark-gray-500';

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, icon, className = '', children }) => (
  <div
    className={`rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-dark-gray-700 dark:bg-dark-gray-800/40 ${className}`}
  >
    <div className="mb-3 flex items-center gap-2">
      <span className="text-green-600 dark:text-green-400">{icon}</span>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400">
        {title}
      </h3>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Stat: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-500 dark:text-dark-gray-400">{label}</span>
    <span className="font-medium text-gray-900 dark:text-white">{children}</span>
  </div>
);

const BadgeRow: React.FC<{
  label: string;
  severity: BillingReadinessSeverity;
  text: string;
  sub?: string | null;
}> = ({ label, severity, text, sub }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-500 dark:text-dark-gray-400">{label}</span>
    <span className="flex items-center gap-2">
      <Badge severity={severity} text={text} />
      {sub ? <span className={`text-xs ${muted}`}>{sub}</span> : null}
    </span>
  </div>
);

const EmptyRow: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-500 dark:text-dark-gray-400">{label}</span>
    <span className={`text-xs ${muted}`}>{text}</span>
  </div>
);

type Props = {
  clinicId: number;
  clinicName: string;
  window: BillingReadinessWindow;
  onClose: () => void;
};

const ICON = 'h-4 w-4';

const BillingReadinessDetailModal: React.FC<Props> = ({
  clinicId,
  clinicName,
  window,
  onClose,
}) => {
  const { data, isFetching } = useBillingReadinessDetail(clinicId, window);

  return (
    <Modal isOpen close={onClose} size="2xl" header={{ title: `${clinicName} · #${clinicId}` }}>
      {!data ? (
        <div className={`p-8 text-center text-sm ${muted}`}>
          {isFetching ? 'Loading…' : 'No detail available.'}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Card title="Claim transmission" icon={<PaperAirplaneIcon className={ICON} />}>
              <Stat label="Total claims (all time)">{data.transmission.totalAllTime}</Stat>
              <Stat label={`Submissions (last ${window}d)`}>{data.transmission.inWindow}</Stat>
              {data.transmission.businessDaysSince == null ? (
                <EmptyRow label="Last submission" text="No history" />
              ) : (
                <BadgeRow
                  label="Last submission"
                  severity={data.transmission.severity}
                  text={`${data.transmission.businessDaysSince} business days`}
                  sub={fmtDate(data.transmission.lastSubmission)}
                />
              )}
            </Card>

            <Card title="Rejections" icon={<ExclamationTriangleIcon className={ICON} />}>
              {!data.rejection.available ? (
                <p className={`text-sm ${muted}`}>Not available — no clearinghouse integration.</p>
              ) : data.rejection.pct == null ? (
                <EmptyRow label="Ratio (window)" text="No data" />
              ) : (
                <BadgeRow
                  label="Ratio (window)"
                  severity={data.rejection.severity}
                  text={`${data.rejection.numerator}/${data.rejection.denominator} = ${data.rejection.pct}%`}
                />
              )}
            </Card>

            <Card title="Denials" icon={<NoSymbolIcon className={ICON} />}>
              {data.denial.pct == null ? (
                <EmptyRow label="Ratio (window)" text="No ERA data" />
              ) : (
                <BadgeRow
                  label="Ratio (window)"
                  severity={data.denial.severity}
                  text={`${data.denial.numerator}/${data.denial.denominator} = ${data.denial.pct}%`}
                />
              )}
              <Stat label="Data source">{data.denial.source ?? '—'}</Stat>
            </Card>

            <Card title="Zero revenue activity" icon={<BanknotesIcon className={ICON} />}>
              {data.lastPayment.daysSince == null ? (
                <EmptyRow label="Last posted payment" text="No payment history" />
              ) : (
                <BadgeRow
                  label="Last posted payment"
                  severity={data.lastPayment.severity}
                  text={`${data.lastPayment.daysSince} days ago`}
                  sub={fmtDate(data.lastPayment.date)}
                />
              )}
              {data.lastEra.daysSince == null ? (
                <EmptyRow label="Last ERA received" text="No ERA history" />
              ) : (
                <BadgeRow
                  label="Last ERA received"
                  severity={data.lastEra.severity}
                  text={`${data.lastEra.daysSince} days ago`}
                  sub={[fmtDate(data.lastEra.date), data.lastEra.source]
                    .filter(Boolean)
                    .join(' · ')}
                />
              )}
            </Card>
          </div>

          {data.rejection.available && data.rejection.codes.length > 0 && (
            <Card title="Top rejection codes" icon={<TagIcon className={ICON} />}>
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-xs uppercase tracking-wider ${muted}`}>
                    <th className="pb-2 font-medium">Code</th>
                    <th className="pb-2 text-right font-medium">Count</th>
                    <th className="pb-2 text-right font-medium">Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rejection.codes.map(c => (
                    <tr
                      key={c.code || 'none'}
                      className="border-t border-gray-100 dark:border-dark-gray-700"
                    >
                      <td className="py-1.5">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {c.code || '—'}
                        </div>
                        {c.description ? (
                          <div className={`text-xs ${muted}`}>{c.description}</div>
                        ) : null}
                      </td>
                      <td className="py-1.5 text-right text-gray-700 dark:text-dark-gray-200">
                        {c.count}
                      </td>
                      <td className="py-1.5 text-right text-gray-700 dark:text-dark-gray-200">
                        {c.pct == null ? '—' : `${c.pct}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BillingReadinessDetailModal;
